import React, { useState } from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { orderItemApi } from '../../../../redux/apis/endpoints/orderItemApi';
import { useDispatch } from 'react-redux';
import ReturnItemInfo from '../ReturnItemInfo';

const CancelGroupReturnModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  entity 
}) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComments, setCancelComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // RTK Query hook for canceling individual item returns
  const [cancelReturnRequest] = orderItemApi.useCancelReturnRequestMutation();

  const orderItems = entity?.orderItems || [];
  const cancellableItems = orderItems.filter(item => 
    ['return_requested_by_customer', 'return_approved_by_merchant'].includes(item.status)
  );

  const handleItemToggle = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === cancellableItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cancellableItems.map(item => item.id)));
    }
  };

  const handleConfirm = async () => {
    if (selectedItems.size === 0) {
      alert('Please select at least one return request to cancel');
      return;
    }

    if (!cancelReason) {
      alert('Please provide a reason for canceling the return');
      return;
    }

    setIsLoading(true);

    try {
      const itemIds = Array.from(selectedItems);
      console.log('🔄 Starting cancel return process:', {
        selectedItemIds: itemIds,
        cancelReason,
        cancelComments,
        fulfillmentGroup: entity
      });

      const promises = itemIds.map(itemId => {
        const requestData = {
          orderItemId: itemId,
          reason: cancelReason,
          comments: cancelComments,
          fulfillmentGroupId: entity.id // Pass FG ID for better cache management
        };
        
        console.log('📦 Canceling return request for item:', requestData);
        
        return cancelReturnRequest(requestData).unwrap();
      });

      const results = await Promise.all(promises);
      console.log('✅ All return cancellations completed successfully:', results);

      // Enhanced cache invalidation to trigger immediate UI updates
      // Invalidate both general lists and specific entities
      dispatch(orderItemApi.util.invalidateTags([
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: entity.id }, // Specific FG
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        // Also invalidate individual order items
        ...itemIds.map(itemId => ({ type: 'OrderItem', id: itemId }))
      ]));

      // Additional manual cache update to ensure FG status is correct
      const { fulfillmentGroupApi } = await import('../../../../redux/apis/endpoints/fulfillmentGroupApi');
      dispatch(
        fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', entity.id, (draft) => {
          if (draft && draft.orderItems) {
            console.log('🔄 Manual cache update for FG after group return cancellation');
            
            // Update status of canceled items
            itemIds.forEach(itemId => {
              const item = draft.orderItems.find(item => item.id === itemId);
              if (item) {
                item.status = 'delivered';
                if (item.returnRequest) {
                  if (typeof item.returnRequest === 'object') {
                    item.returnRequest.status = 'canceled';
                  } else {
                    // if stored as ID, convert to object with status field
                    item.returnRequest = { id: item.returnRequest, status: 'canceled' };
                  }
                }
              }
            });
            
            // Determine new FG status based on remaining item statuses
            const remainingReturnRequests = draft.orderItems.filter(item => 
              item.status === 'return_requested_by_customer' || 
              item.status === 'return_approved_by_merchant'
            ).length;
            
            if (remainingReturnRequests === 0) {
              // No more return requests - determine completed status based on delivery type
              const hasPickupItems = draft.orderItems.some(item => item.status === 'pickup_completed_by_customer');
              const hasDeliveredItems = draft.orderItems.some(item => item.status === 'delivered');
              
              if (hasPickupItems && !hasDeliveredItems) {
                draft.status = 'pickup_completed_by_customer';
              } else if (hasDeliveredItems && !hasPickupItems) {
                draft.status = 'delivery_completed_to_customer';
              } else {
                // Mixed or default case
              draft.status = 'delivery_completed_to_customer';
              }
              console.log(`✅ Updated FG status to ${draft.status} after group cancellation`);
            } else if (remainingReturnRequests < draft.orderItems.length) {
              draft.status = 'partial_return_requested';
              console.log('🔄 Updated FG status to partial_return_requested after partial cancellation');
            }
          }
        })
      );

      onConfirm({
        success: true,
        message: `Return cancellation completed for ${itemIds.length} item(s)`,
        results
      });

      onClose();
    } catch (error) {
      console.error('❌ Failed to cancel return requests:', {
        error,
        errorMessage: error?.message,
        errorData: error?.data,
        status: error?.status
      });
      
      onConfirm({
        success: false,
        message: error?.data?.message || error?.message || 'Failed to cancel return requests'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedItems(new Set());
    setCancelReason('');
    setCancelComments('');
    onClose();
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'return_requested_by_customer':
        return 'Return Requested';
      case 'return_approved_by_merchant':
        return 'Return Approved';
      default:
        return status;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cancel Return"
      size="lg"
    >
      <div className="py-4">
        <div className="flex items-center mb-4">
          <XMarkIcon className="h-12 w-12 text-red-500 mr-4" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Cancel Return Requests
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Select the return requests you'd like to cancel and provide a reason.
            </p>
          </div>
        </div>

        {cancellableItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No return requests are available to cancel.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Item Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select return requests to cancel
                </label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-brand-teal hover:text-brand-teal-dark"
                >
                  {selectedItems.size === cancellableItems.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                {cancellableItems.map((item) => (
                  <div key={item.id} className="p-3">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleItemToggle(item.id)}
                        className="mt-1 h-4 w-4 text-brand-teal focus:ring-brand-teal border-gray-300 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <ReturnItemInfo item={item} statusText={getStatusText(item.status)} />
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Cancel Reason */}
            <div>
              <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for canceling return *
              </label>
              <select
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                required
              >
                <option value="">Select a reason</option>
                <option value="changed_mind">Changed my mind about returning</option>
                <option value="issue_resolved">Issue was resolved</option>
                <option value="want_to_keep">Decided to keep the item</option>
                <option value="wrong_item_selected">Selected wrong item by mistake</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Cancel Comments */}
            <div>
              <label htmlFor="cancel-comments" className="block text-sm font-medium text-gray-700 mb-2">
                Additional details (optional)
              </label>
              <textarea
                id="cancel-comments"
                value={cancelComments}
                onChange={(e) => setCancelComments(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                placeholder="Please provide any additional details about canceling your return..."
              />
            </div>
          </div>
        )}

        {selectedItems.size > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mt-4">
            <h4 className="text-sm font-medium text-red-900 mb-2">
              Important:
            </h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• Canceling return requests will restore the items to their delivered status</li>
              <li>• You will no longer be able to return these items unless you create a new return request</li>
              <li>• This action cannot be undone</li>
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <Button
            onClick={handleConfirm}
            disabled={isLoading || selectedItems.size === 0 || !cancelReason}
            isLoading={isLoading}
            variant="danger"
            className="flex items-center justify-center"
          >
            <XMarkIcon className="h-4 w-4 mr-2" />
            Cancel Return ({selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''})
          </Button>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant="secondary"
          >
            Keep Returns
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelGroupReturnModal; 